import { useState } from 'react';
import { useT, useUiStore } from '@/store';
import { useSetupStatus } from '@/hooks/useTenant';
import { Panel } from '@/components/ui/Panel';

const COLLAPSED_KEY = 'aragela.setupChecklist.collapsed';

/**
 * What a new company has to do before this console's numbers mean anything.
 *
 * P1-2: a new tenant signed in and got a blank map, a blank order table and
 * a blank Analytics tab. Nothing said that quarries, sites, prices and
 * drivers have to be entered first, or in what order, so every install
 * needed the person who built the product — "done-for-you" became
 * "done-by-you-forever".
 *
 * It is a progress bar with links, not a tutorial. Each line names what is
 * missing, what it costs to leave it missing, and opens the tab it is
 * finished on. The steps render in the order the backend gives them, which
 * is the order the work has to happen in: a price needs a quarry to belong
 * to, a dispatch needs somewhere to go.
 *
 * SOME STEPS ARE NOT COUNTS, and that is the part worth reading twice.
 * tenants.fuel_cost_per_km ships at 0.35 EUR/km — a number schema.sql
 * picked — and it multiplies into every margin figure on the Analytics tab.
 * A checklist that ticked that step because a value was present would be
 * reporting a decision nobody made. The backend tests a *_configured_at
 * stamp instead, so those steps mean "somebody answered", and the panel
 * shows the figure the install is currently running on beside them.
 *
 * Renders nothing once every step is done. It does not linger, and there is
 * no dismiss: a checklist a dispatcher can dismiss while the fuel cost is
 * still a guess is a checklist that has stopped saying anything. It does
 * COLLAPSE to its progress line — the daily-use case, where a dispatcher
 * knows perfectly well what is outstanding and needs the screen. That
 * choice is per-browser (localStorage), because it is a preference about
 * this screen, not a fact about the company.
 */
export function SetupChecklist() {
  const t = useT();
  const setView = useUiStore((s) => s.setView);
  const { data } = useSetupStatus();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(COLLAPSED_KEY) === '1';
    } catch {
      // Private mode, or storage disabled. Expanded is the safe default:
      // the panel's job is to be read.
      return false;
    }
  });

  if (!data || data.complete) return null;

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      window.localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
    } catch {
      // Nothing to do — the panel still works, it just forgets next time.
    }
  };

  const pct = Math.round((data.done / data.total) * 100);

  return (
    <Panel
      title={t('setup.title')}
      actions={
        <button type="button" className="btn !px-2.5 !py-1 !text-[11px]" onClick={toggle}>
          {t(collapsed ? 'setup.expand' : 'setup.collapse')}
        </button>
      }
    >
      <div className="flex items-center gap-3">
        <div
          className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/8"
          role="progressbar"
          aria-valuenow={data.done}
          aria-valuemin={0}
          aria-valuemax={data.total}
          aria-label={t('setup.title')}
        >
          <div className="h-full rounded-full bg-accent transition-[width]" style={{ width: `${pct}%` }} />
        </div>
        <span className="shrink-0 text-[12px] font-semibold tabular-nums">
          {t('setup.progress', { done: data.done, total: data.total })}
        </span>
      </div>

      {collapsed ? null : (
        <>
          <p className="mt-2.5 text-[11px] leading-snug text-dim" role="note">
            {t('setup.intro')}
          </p>
          <ul className="mt-2.5 space-y-1.5">
            {data.steps.map((step) => (
              <li
                key={step.key}
                className="flex flex-wrap items-center justify-between gap-2 rounded-[11px] border border-line bg-white/2 px-3 py-2"
              >
                <div className="flex min-w-0 items-start gap-2.5">
                  <span
                    aria-hidden="true"
                    className={`mt-[3px] grid h-4 w-4 shrink-0 place-items-center rounded-full text-[10px] font-bold ${
                      step.done ? 'bg-green/20 text-green' : 'border border-line text-dim'
                    }`}
                  >
                    {step.done ? '✓' : ''}
                  </span>
                  <div className="min-w-0">
                    <div className={`text-[12.5px] font-semibold ${step.done ? 'text-dim' : ''}`}>
                      {t(`setup.step.${step.key}`)}
                      {step.count != null && step.done ? (
                        <span className="ml-1.5 font-normal text-dim tabular-nums">({step.count})</span>
                      ) : null}
                    </div>
                    {step.done ? null : (
                      <div className="mt-0.5 text-[11px] leading-snug text-dim">
                        {t(`setup.why.${step.key}`, {
                          fuel: data.fuel_cost_per_km ?? '—',
                        })}
                      </div>
                    )}
                  </div>
                </div>
                {step.done ? null : (
                  <button
                    type="button"
                    className="btn !px-2.5 !py-1 !text-[11px] shrink-0"
                    onClick={() => setView(step.view)}
                  >
                    {t('setup.open')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </Panel>
  );
}
